import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import brandLogo from "@/assets/regard-de-nature-logo.png";
import { BRAND } from "@/config/brand";
import {
  Calendar,
  Phone,
  Check,
  X,
  Eye,
  MessageCircle,
  ClipboardList,
  Sparkles,
  RefreshCcw,
  Lock,
  ArrowLeft,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Mail } from "lucide-react";

const MonApproche = () => {
  const [contactDialogOpen, setContactDialogOpen] = React.useState(false);

  const scrollToAccompagnements = () => {
    window.location.href = "/#accompagnements";
  };

  const etapes = [
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "Échange et compréhension",
      description: "Compréhension globale de votre situation",
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Observation du terrain",
      description: "Dont iridologie si pertinente",
    },
    {
      icon: <ClipboardList className="w-6 h-6" />,
      title: "Recommandations",
      description: "Personnalisées et adaptées",
    },
    {
      icon: <RefreshCcw className="w-6 h-6" />,
      title: "Suivi",
      description: "Ajustements dans le temps",
    },
  ];

  const ceQuiEstPourVous = [
    "Vous souhaitez mieux comprendre votre fonctionnement",
    "Vous recherchez une approche naturelle structurée",
    "Vous êtes prêt(e) à vous impliquer dans votre accompagnement",
    "Vous cherchez de la clarté plutôt que des promesses",
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur-sm">
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img src={brandLogo} alt={BRAND.name.full} className="w-9 sm:w-10 md:w-12 h-auto object-contain" />
            <div className="text-left">
              <div className="font-cinzel font-bold leading-tight">
                <span className="text-secondary text-sm sm:text-base md:text-lg">{BRAND.name.line1}</span>
                <span className="text-primary text-sm sm:text-base md:text-lg"> {BRAND.name.line2}</span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] font-montserrat uppercase tracking-wide">
                <span className="text-primary">{BRAND.baseline.parts.text1}</span>
                <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
                <span className="text-primary">{BRAND.baseline.parts.text2}</span>
              </p>
            </div>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/">
              <Button variant="ghost" size="sm" className="font-montserrat">
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Retour</span>
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground hover:text-primary font-montserrat"
              >
                <Lock className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Espace Pro</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* BLOC 1 — Introduction */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-cinzel font-bold text-primary leading-tight">
              Une approche fondée sur l'observation du terrain
            </h1>
            <div className="space-y-4 text-base sm:text-lg text-foreground/80 font-montserrat">
              <p>Chaque personne est unique.</p>
              <p>
                Mon accompagnement repose sur l'observation et la compréhension de votre terrain, afin de vous proposer
                des recommandations naturopathiques cohérentes, progressives et adaptées à votre rythme.
              </p>
              <p>
                L'objectif n'est pas de corriger un symptôme isolé, mais de mieux comprendre votre fonctionnement
                global.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BLOC 2 — Ce que je propose / Ce que je ne propose pas */}
      <section className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary text-center mb-10 sm:mb-14">
            Un cadre clair et respectueux
          </h2>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {/* Ce que je propose */}
            <Card className="border-primary/20 bg-primary-light/30">
              <CardContent className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-primary mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                  Ce que je propose
                </h3>
                <ul className="space-y-4 font-montserrat">
                  {[
                    "Une approche globale et individualisée",
                    "Un accompagnement naturopathique structuré",
                    "L'iridologie comme outil d'observation du terrain",
                    "Des recommandations personnalisées et évolutives",
                    "Un suivi dans le temps, selon vos besoins",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Ce que je ne propose pas */}
            <Card className="border-muted-foreground/20 bg-muted/30">
              <CardContent className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-muted-foreground mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <X className="w-5 h-5 text-muted-foreground" />
                  </div>
                  Ce que je ne propose pas
                </h3>
                <ul className="space-y-4 font-montserrat">
                  {[
                    "Aucun diagnostic médical",
                    "Aucune promesse de guérison",
                    "Aucune solution miracle",
                    "Aucune substitution au suivi médical",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <X className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* BLOC 3 — L'iridologie expliquée simplement */}
      <section className="py-12 sm:py-16 md:py-20 bg-muted/20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Eye className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary">
                L'iridologie : un outil d'observation du terrain
              </h2>
              <div className="space-y-4 text-base sm:text-lg text-foreground/80 font-montserrat text-left">
                <p>
                  L'iridologie consiste à observer l'iris, partie colorée de l'œil, afin de mieux comprendre les
                  tendances du terrain et la vitalité globale.
                </p>
                <p>
                  Elle ne permet pas de poser un diagnostic médical, mais aide à affiner l'accompagnement naturopathique
                  en mettant en lumière certaines zones de sensibilité et les capacités d'adaptation de l'organisme.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOC 4 — Déroulement de l'accompagnement */}
      <section className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary text-center mb-10 sm:mb-14">
            Comment se déroule l'accompagnement
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {etapes.map((etape, index) => (
              <div key={index} className="flex flex-col items-center text-center space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground">
                    {etape.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-sm font-bold font-montserrat">
                    {index + 1}
                  </div>
                </div>
                <div>
                  <h3 className="font-cinzel font-semibold text-primary text-lg mb-1">{etape.title}</h3>
                  <p className="text-sm text-muted-foreground font-montserrat">{etape.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOC 5 — À qui s'adresse cette approche */}
      <section className="py-12 sm:py-16 md:py-20 bg-muted/20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col items-center text-center mb-10">
              <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-secondary" />
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary">
                Cette approche est faite pour vous si…
              </h2>
            </div>

            <div className="space-y-4">
              {ceQuiEstPourVous.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border shadow-sm"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-foreground/80 font-montserrat">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BLOC 6 — Appel à l'action */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            <p className="text-lg sm:text-xl text-foreground/80 font-montserrat">
              Si cette approche résonne pour vous, vous pouvez prendre rendez-vous ou me contacter pour échanger.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 sm:h-14 px-6 sm:px-8 font-montserrat"
                onClick={scrollToAccompagnements}
              >
                <Calendar className="w-5 h-5 mr-2" />
                Prendre rendez-vous
              </Button>

              <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 font-montserrat"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Me contacter
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-cinzel text-primary">Me contacter</DialogTitle>
                    <DialogDescription className="font-montserrat">
                      Choisissez votre mode de contact préféré
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <a
                      href="tel:+33000000000"
                      className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-primary font-montserrat">Par téléphone</p>
                        <p className="text-sm text-muted-foreground font-montserrat">06 XX XX XX XX</p>
                      </div>
                    </a>
                    <a
                      href="mailto:contact@regard-de-nature.fr"
                      className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-12 h-12 bg-secondary-light rounded-lg flex items-center justify-center text-secondary flex-shrink-0">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-primary font-montserrat">Par email</p>
                        <p className="text-sm text-muted-foreground font-montserrat">contact@regard-de-nature.fr</p>
                      </div>
                    </a>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="py-6 border-t border-border bg-card">
        <div className="container mx-auto px-4 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary font-montserrat">
            ← Retour à l'accueil
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default MonApproche;
